import React from 'react';
import { z } from 'zod';
import type { StoryMeta } from '@features/devtools/registry/types';
import {
  HeadlessSchemaConfigForm,
  type SchemaConfigMeta,
  type HeadlessSchemaConfigFormProps,
} from './HeadlessSchemaConfigForm';
import { shadcnAdapter } from '@shared/ui/adapters';

type Props = React.ComponentProps<typeof HeadlessSchemaConfigForm<any>>;

function SchemaConfigFormStoryWrapper(props: Props) {
  const { meta, value, adapter = shadcnAdapter, ...rest } = props as Props & {
    adapter?: Props['adapter'];
  };
  const [current, setCurrent] = React.useState(value);

  React.useEffect(() => {
    setCurrent(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  return (
    <div className="grid gap-4">
      <HeadlessSchemaConfigForm
        meta={meta}
        value={current}
        onChange={(next) => setCurrent(next)}
        adapter={adapter}
        {...rest}
      />
      <section className="rounded-md border p-3 bg-muted/30">
        <div className="text-sm font-medium">Meta</div>
        <pre className="mt-1 text-xs whitespace-pre-wrap">{JSON.stringify(meta, null, 2)}</pre>
      </section>
      <section className="rounded-md border p-3 bg-muted/30">
        <div className="text-sm font-medium">Current Value (includes _version)</div>
        <pre className="mt-1 text-xs whitespace-pre-wrap">{JSON.stringify(current, null, 2)}</pre>
      </section>
    </div>
  );
}

// Demo schemas
const basicSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  count: z.number().int().min(0).max(100).default(0),
  isActive: z.boolean().default(true),
  level: z.enum(['info', 'warn', 'error']).default('info'),
  role: z.enum(['admin', 'editor', 'viewer']).default('viewer'),
  roles: z.array(z.enum(['admin', 'editor', 'viewer'])).default([]),
  tags: z.array(z.string()).default([]),
  loaders: z
    .array(
      z.object({
        id: z.string().min(1),
        key: z.string().min(1),
        writeMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).optional(),
        transform: z.string().optional(),
      }),
    )
    .default([]),
});

const buttonSchema = z.object({
  label: z.string().min(1),
  intent: z.enum(['default', 'destructive', 'outline', 'secondary', 'ghost', 'link']).default('default'),
  size: z.enum(['sm', 'default', 'lg', 'icon']).default('default'),
  disabled: z.boolean().default(false),
});

const stringFormatsSchema = z.object({
  email: z.string().email().describe('Email format'),
  url: z.string().url().describe('URL format'),
  uuid: z.string().uuid().describe('UUID format'),
  optionalNote: z.string().optional().describe('Optional string (not required)'),
  nullableComment: z.string().nullable().describe('Nullable string (oneOf with null)'),
});

const basicMeta: SchemaConfigMeta = {
  id: 'demo',
  title: 'Demo Config',
  description: 'Basic fields + loaders array to exercise uiSchema helpers.',
  version: 1,
  schema: basicSchema,
};

const buttonMeta: SchemaConfigMeta = {
  id: 'button',
  title: 'Button Config',
  description: 'Shows select defaults for intent/size when meta.id === "button".',
  version: 1,
  schema: buttonSchema,
};

const meta: StoryMeta<React.ComponentProps<typeof SchemaConfigFormStoryWrapper>> = {
  id: 'forms/HeadlessSchemaConfigForm',
  title: 'Headless Schema Config Form',
  group: 'Shared/Forms',
  component: SchemaConfigFormStoryWrapper as any,
  description:
    'Headless RJSF form that adapts to UI libraries via an adapter. Demonstrates Zod→JSON Schema, uiSchema rules, and _version attach/detach.',
  defaultProps: {
    meta: basicMeta,
    value: {
      _version: 1,
      title: 'Hello World',
      count: 5,
      isActive: true,
      level: 'info',
      tags: ['alpha', 'beta'],
      loaders: [
        { id: 'project', key: '/api/projects/:id', writeMethod: 'GET' },
        { id: 'comments', key: '/api/projects/:id/comments', writeMethod: 'GET' },
      ],
    },
    enableArrayControls: true,
    disabled: false,
  },
  controls: {
    disabled: { type: 'boolean', label: 'Disabled' },
    enableArrayControls: { type: 'boolean', label: 'Enable Array Controls' },
    meta: { type: 'json', label: 'Meta' },
    value: { type: 'json', label: 'Value' },
  },
  variants: [
    {
      name: 'Basic',
      props: {
        meta: basicMeta,
        value: {
          _version: 1,
          title: 'Welcome',
          count: 0,
          isActive: true,
          level: 'warn',
          tags: [],
          loaders: [],
        },
        enableArrayControls: false,
      },
    },
    {
      name: 'With Loaders UI',
      props: {
        meta: basicMeta,
        value: {
          _version: 1,
          title: 'Data Page',
          count: 10,
          isActive: true,
          level: 'info',
          role: 'editor',
          roles: ['viewer'],
          tags: ['list'],
          loaders: [
            { id: 'users', key: '/api/users', writeMethod: 'GET' },
            { id: 'createUser', key: '/api/users', writeMethod: 'POST', transform: 'shapeUser' },
          ],
        },
        enableArrayControls: true,
      },
    },
    {
      name: 'Button Config (Segmented)',
      props: {
        meta: buttonMeta,
        value: { _version: 1, label: 'Click me', intent: 'default', size: 'default', disabled: false },
        enableArrayControls: false,
      },
    },
    {
      name: 'String Formats & Nullability',
      props: {
        meta: {
          id: 'formats',
          title: 'String Formats',
          description: 'Email, URL, UUID, optional vs nullable',
          version: 1,
          schema: stringFormatsSchema,
        },
        value: {
          _version: 1,
          email: 'hello@example.com',
          url: 'https://example.com',
          uuid: '00000000-0000-0000-0000-000000000000',
          optionalNote: undefined,
          nullableComment: null,
        },
      },
    },
  ],
};

export default meta;
